import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

import { Brand, Callout, ErrorCallout, Mark, Spinner, sentence } from '../components/ui';
import { identify, track } from '../lib/analytics';
import { api, ApiError, asApiError } from '../lib/api';
import { continueWith, finishSocialSignIn, socialSignIn, type SocialProvider } from '../lib/clerk';
import { money, perSecond } from '../lib/format';
import { useDocumentTitle } from '../lib/hooks';
import { forgetLinkToken, keepLinkToken, linkToken } from '../lib/link-token';
import { useSession } from '../lib/session';
import type { Pricing } from '../lib/types';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD = 10;

/* Under the card: what it costs, from the plane's own price list. */
function PriceNote() {
  const [pricing, setPricing] = useState<Pricing | null>(null);
  useEffect(() => {
    api<Pricing>('/v1/pricing').then(setPricing, () => setPricing(null));
  }, []);
  return (
    <p className="auth__note">
      <span>{pricing ? `${perSecond(pricing.usdPerSecond)} a second ($${pricing.usdPerHour} an hour)` : '$0.000217 a second ($0.78 an hour)'}</span>
      <span>{pricing ? `${money(pricing.freeCreditUsd)} of free credit, no card needed` : '$5.00 of free credit, no card needed'}</span>
    </p>
  );
}

function PasswordInput({ id, value, onChange, autoComplete, invalid, describedBy, autoFocus }: { id: string; value: string; onChange: (v: string) => void; autoComplete: string; invalid?: boolean; describedBy?: string; autoFocus?: boolean }) {
  const [show, setShow] = useState(false);
  return (
    <div className="pw">
      <input
        id={id}
        className="input"
        type={show ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        autoFocus={autoFocus}
      />
      <button type="button" className="btn btn--quiet btn--icon" onClick={() => setShow((s) => !s)} aria-label={show ? 'Hide password' : 'Show password'}>
        {show ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
      </button>
    </div>
  );
}

/** A `next` that stays on this site, else the console's home. A backslash is refused: browsers read `/\host` as `//host`. */
export function safeNext(next: string | null): string {
  return next && next.startsWith('/') && !next.startsWith('//') && !next.includes('\\') ? next : '/';
}

function GitHubMark() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true" fill="currentColor">
      <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
    </svg>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 18 18" aria-hidden="true">
      <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92a8.78 8.78 0 0 0 2.68-6.62Z" />
      <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.32-1.58-5.03-3.7H.96v2.33A9 9 0 0 0 9 18Z" />
      <path fill="#FBBC05" d="M3.97 10.72A5.4 5.4 0 0 1 3.68 9c0-.6.1-1.18.29-1.72V4.95H.96A9 9 0 0 0 0 9c0 1.45.35 2.83.96 4.05l3.01-2.33Z" />
      <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .96 4.95l3.01 2.33C4.68 5.16 6.66 3.58 9 3.58Z" />
    </svg>
  );
}

const PROVIDER_NAME: Record<SocialProvider, string> = { github: 'GitHub', google: 'Google' };

/* The social providers this plane offers (none without Clerk); empty until it answers. */
function useSocialProviders(): SocialProvider[] {
  const [providers, setProviders] = useState<SocialProvider[]>([]);
  useEffect(() => {
    socialSignIn().then((answer) => setProviders(answer.providers));
  }, []);
  return providers;
}

/* "Sign in with Google or your email.", "… a social provider …" for both, "… your email." for neither. */
function withWhat(providers: SocialProvider[]): string {
  if (providers.length === 0) return 'your email';
  return `${providers.length === 1 ? PROVIDER_NAME[providers[0]!] : 'a social provider'} or your email`;
}

/* A button per offered provider; nothing when there are none, so a plane
   without Clerk shows the email form alone. */
function SocialButtons({ next, providers }: { next: string; providers: SocialProvider[] }) {
  const [busy, setBusy] = useState<SocialProvider | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  if (providers.length === 0) return null;

  async function go(provider: SocialProvider) {
    setBusy(provider);
    setError(null);
    track('social_signin_started', { provider });
    try {
      await continueWith(provider, next);
    } catch (err) {
      setError(asApiError(err));
      setBusy(null);
    }
  }

  return (
    <>
      {error ? <ErrorCallout error={error} title="That sign-in could not start." /> : null}
      <div className="auth-card__social">
        {providers.map((provider) => (
          <button key={provider} type="button" className="btn btn--lg" onClick={() => go(provider)} disabled={busy !== null} aria-busy={busy === provider}>
            {busy === provider ? <Spinner /> : provider === 'github' ? <GitHubMark /> : <GoogleMark />}
            Continue with {PROVIDER_NAME[provider]}
          </button>
        ))}
      </div>
      <p className="auth-card__or" role="separator">
        <span>or</span>
      </p>
    </>
  );
}

function Terms({ verb }: { verb: string }) {
  return (
    <p className="auth-card__terms">
      By {verb}, you agree to the <a href="https://offstage.cloud/terms">Terms</a> and the <a href="https://offstage.cloud/privacy">Privacy</a> policy.
    </p>
  );
}

/* Email first, then the password, the way most sign-ins go: the first step
   says nothing about whether the address has an account. */
export function SignIn() {
  useDocumentTitle('Sign in');
  const social = useSocialProviders();
  const { refreshMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [step, setStep] = useState<'email' | 'password'>('email');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const next = safeNext(params.get('next'));
  const emailOk = EMAIL.test(email.trim());
  const emailError = (error?.code === 'bad-email' ? sentence(error.message) : null) ?? (touched && !emailOk ? 'Enter an email address, like you@company.com.' : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!emailOk) return;
    if (step === 'email') {
      setStep('password');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api('/v1/auth/login', { method: 'POST', body: { email: email.trim(), password } });
      identify(email.trim());
      track('signin_completed', { method: 'email' });
      await refreshMe();
      navigate(next, { replace: true });
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <main className="auth__form">
        <div className="auth-card">
          <Mark className="auth-card__mark" />
          <div>
            <h1>Sign in</h1>
            <p className="lede">Sign in with {withWhat(social)}.</p>
          </div>
          <SocialButtons next={next} providers={social} />
          <form onSubmit={submit} noValidate>
            {error && error.code !== 'bad-email' ? (
              error.code === 'bad-credentials' ? (
                <Callout tone="error" role="alert" title="That email and password don't match an account.">
                  Check both and try again, <Link to="/forgot-password" state={{ email: email.trim() }}>recover your account</Link>, or <Link to="/sign-up">sign up</Link>.
                </Callout>
              ) : (
                <ErrorCallout error={error} />
              )
            ) : null}
            <div className="field">
              <div className="auth-card__label">
                <label htmlFor="email">Email</label>
                <Link to="/forgot-password" state={{ email: email.trim() }}>
                  Recover account
                </Link>
              </div>
              <input
                id="email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="username"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (step === 'password') setStep('email');
                }}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'email-error' : undefined}
                required
                autoFocus
              />
              {emailError ? (
                <p className="field-error" id="email-error" style={{ margin: 0 }}>
                  {emailError}
                </p>
              ) : null}
            </div>
            {step === 'password' ? (
              <div className="field">
                <label htmlFor="password">Password</label>
                <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="current-password" autoFocus />
              </div>
            ) : null}
            <button className="btn btn--primary btn--lg" type="submit" disabled={busy || (step === 'password' && !password)} aria-busy={busy}>
              {busy ? <Spinner /> : null}
              {step === 'email' ? 'Continue' : 'Sign in'}
            </button>
          </form>
          <p className="auth__switch">
            Don't have an account? <Link to="/sign-up">Sign up</Link>.
          </p>
          <Terms verb="continuing" />
        </div>
        <PriceNote />
      </main>
    </div>
  );
}

export function SignUp() {
  useDocumentTitle('Sign up');
  const social = useSocialProviders();
  const { refreshMe } = useSession();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const emailOk = EMAIL.test(email.trim());
  const passwordOk = password.length >= MIN_PASSWORD;
  const fieldError = (code: string) => (error && error.code === code ? sentence(error.message) : null);
  const emailError = fieldError('bad-email') ?? (touched && !emailOk ? 'Enter an email address, like you@company.com.' : null);
  const passwordError = fieldError('weak-password') ?? (touched && !passwordOk ? 'Use at least 10 characters.' : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!emailOk || !passwordOk) return;
    setBusy(true);
    setError(null);
    try {
      await api('/v1/auth/signup', { method: 'POST', body: { email: email.trim(), password } });
      identify(email.trim());
      track('signup_completed', { method: 'email' });
      await refreshMe();
      navigate('/', { replace: true });
    } catch (err) {
      const failed = asApiError(err);
      track('signup_failed', { code: failed.code });
      setError(failed);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => track('signup_viewed'), []);
  const formLevel = error && !['bad-email', 'weak-password'].includes(error.code) ? error : null;

  return (
    <div className="auth">
      <main className="auth__form">
        <div className="auth-card">
          <Mark className="auth-card__mark" />
          <div>
            <h1>Sign up</h1>
            <p className="lede">Sign up with {withWhat(social)}.</p>
          </div>
          <SocialButtons next="/" providers={social} />
          <form onSubmit={submit} noValidate>
            {formLevel ? (
              formLevel.code === 'email-taken' ? (
                <Callout tone="error" role="alert" title={`There's already an account for ${email.trim()}.`}>
                  <Link to="/sign-in">Sign in instead</Link>, or use another email.
                </Callout>
              ) : (
                <ErrorCallout error={formLevel} />
              )
            ) : null}
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'email-error' : undefined}
                required
              />
              {emailError ? (
                <p className="field-error" id="email-error" style={{ margin: 0 }}>
                  {emailError}
                </p>
              ) : null}
            </div>
            <div className="field">
              <label htmlFor="password">Password</label>
              <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" invalid={!!passwordError} describedBy="password-hint" />
              <p className={passwordError ? 'field-error' : 'hint'} id="password-hint" style={{ margin: 0 }}>
                {passwordError ?? (password.length > 0 && passwordOk ? 'Long enough.' : 'At least 10 characters.')}
              </p>
            </div>
            <button className="btn btn--primary btn--lg" type="submit" disabled={busy} aria-busy={busy}>
              {busy ? <Spinner /> : null}
              Create account
            </button>
          </form>
          <p className="auth__switch">
            Already have an account? <Link to="/sign-in">Sign in</Link>.
          </p>
          <Terms verb="signing up" />
        </div>
        <PriceNote />
      </main>
    </div>
  );
}

/* Where GitHub and Google send the browser back (through Clerk). It trades
   the Clerk session for the plane's, then goes where the sign-in started. */
export function SsoCallback() {
  useDocumentTitle('Signing in');
  const { refreshMe } = useSession();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState<ApiError | null>(null);
  const started = useRef(false);

  useEffect(() => {
    /* Once: the trade spends the Clerk session, so a second run would only fail. */
    if (started.current) return;
    started.current = true;
    finishSocialSignIn()
      .then(async ({ created, email }) => {
        identify(email);
        track(created ? 'signup_completed' : 'signin_completed', { method: 'social' });
        await refreshMe();
        navigate(created ? '/' : safeNext(params.get('next')), { replace: true });
      })
      .catch((err: unknown) => {
        const failed = asApiError(err);
        track('social_signin_failed', { code: failed.code });
        setError(failed);
      });
  }, [navigate, params, refreshMe]);

  return (
    <div className="auth">
      <main className="auth__form">
        <div className="verify">
          <Brand to="/sign-in" />
          {error ? (
            <>
              <ErrorCallout error={error} title="That sign-in didn't finish." />
              <p className="auth__switch">
                <Link to="/sign-in">Back to sign in</Link>
              </p>
            </>
          ) : (
            <p className="lede" role="status">
              <Spinner /> Signing you in…
            </p>
          )}
          <div id="clerk-captcha" />
        </div>
      </main>
    </div>
  );
}

/* Asks for a reset link. The plane answers the same whether or not the
   address has an account, so this page always says the same thing. */
export function ForgotPassword() {
  useDocumentTitle('Reset your password');
  const location = useLocation();
  const [email, setEmail] = useState(() => {
    const carried = (location.state as { email?: unknown } | null)?.email;
    return typeof carried === 'string' ? carried : '';
  });
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);

  const emailOk = EMAIL.test(email.trim());
  const emailError = (error?.code === 'bad-email' ? sentence(error.message) : null) ?? (touched && !emailOk ? 'Enter an email address, like you@company.com.' : null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!emailOk) return;
    setBusy(true);
    setError(null);
    try {
      const answer = await api<{ sentTo: string }>('/v1/auth/forgot-password', { method: 'POST', body: { email: email.trim() } });
      track('password_reset_requested');
      setSentTo(answer.sentTo);
    } catch (err) {
      setError(asApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <main className="auth__form">
        <div className="auth-card">
          <Mark className="auth-card__mark" />
          <form onSubmit={submit} noValidate>
            <div>
              <h1>Reset your password</h1>
              <p className="lede">We'll email you a link to choose a new one.</p>
            </div>
            {sentTo ? (
              <Callout tone="ok" role="status" title="Check your email.">
                If there's an account for {sentTo}, a link to reset its password is on its way. It works once and expires in an hour.
              </Callout>
            ) : error && error.code !== 'bad-email' ? (
              <ErrorCallout error={error} />
            ) : null}
            <div className="field">
              <label htmlFor="email">Email</label>
              <input
                id="email"
                className="input"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-invalid={emailError ? true : undefined}
                aria-describedby={emailError ? 'email-error' : undefined}
                required
                autoFocus
              />
              {emailError ? (
                <p className="field-error" id="email-error" style={{ margin: 0 }}>
                  {emailError}
                </p>
              ) : null}
            </div>
            <button className="btn btn--primary btn--lg" type="submit" disabled={busy} aria-busy={busy}>
              {busy ? <Spinner /> : null}
              {sentTo ? 'Send it again' : 'Email me a link'}
            </button>
            <p className="auth__switch">
              Remembered it? <Link to="/sign-in">Sign in</Link>
            </p>
          </form>
        </div>
      </main>
    </div>
  );
}

/* The page the emailed reset link opens: /reset-password#token=…. Like
   /verify, the token rides in the fragment and is read once. A too-short
   password leaves the link usable, so the person just tries again. */
export function ResetPassword() {
  useDocumentTitle('Choose a new password');
  const { refreshMe } = useSession();
  const navigate = useNavigate();
  /* Kept until it is spent, so a reload still works; out of the address bar either way (see lib/link-token). */
  const [token] = useState(linkToken);
  useEffect(() => {
    if (token !== null && window.location.hash) keepLinkToken(token);
  }, [token]);
  const [password, setPassword] = useState('');
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const passwordOk = password.length >= MIN_PASSWORD;
  const passwordError = (error?.code === 'weak-password' ? sentence(error.message) : null) ?? (touched && !passwordOk ? `Use at least ${MIN_PASSWORD} characters.` : null);
  const dead = token === null || error?.code === 'bad-token';

  async function submit(e: FormEvent) {
    e.preventDefault();
    setTouched(true);
    if (!passwordOk) return;
    setBusy(true);
    setError(null);
    try {
      await api('/v1/auth/reset-password', { method: 'POST', body: { token, password } });
      forgetLinkToken();
      track('password_reset_completed');
      await refreshMe();
      navigate('/', { replace: true });
    } catch (err) {
      const failed = asApiError(err);
      if (failed.code === 'bad-token') forgetLinkToken();
      setError(failed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <main className="auth__form">
        {dead ? (
          <div className="verify">
            <Brand to="/sign-in" />
            <Callout tone="error" role="alert" title="That link doesn't work anymore.">
              Reset links work once and expire after an hour. <Link to="/forgot-password">Ask for a new one</Link>.
            </Callout>
          </div>
        ) : (
          <form onSubmit={submit} noValidate>
            <Brand to="/sign-in" />
            <div>
              <h1>Choose a new password</h1>
              <p className="lede">This signs you out everywhere else. Your API keys keep working.</p>
            </div>
            {error && error.code !== 'weak-password' ? <ErrorCallout error={error} /> : null}
            <div className="field">
              <label htmlFor="password">New password</label>
              <PasswordInput id="password" value={password} onChange={setPassword} autoComplete="new-password" invalid={!!passwordError} describedBy="password-hint" />
              <p className={passwordError ? 'field-error' : 'hint'} id="password-hint" style={{ margin: 0 }}>
                {passwordError ?? (password.length > 0 && passwordOk ? 'Long enough.' : `At least ${MIN_PASSWORD} characters.`)}
              </p>
            </div>
            <button className="btn btn--primary btn--lg" type="submit" disabled={busy} aria-busy={busy}>
              {busy ? <Spinner /> : null}
              Set password and sign in
            </button>
          </form>
        )}
      </main>
    </div>
  );
}
